import React, {useId, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Plus, Binoculars, Target, Plugs, PencilSimple} from '../../icons';
import {PLATFORMS_CARD_ID} from './panelAnchors';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {CHANNEL_INFO_BY_CHANNEL, ChannelsHelper, FACEBOOK_CHANNEL} from '../../channels/channels';
import {monitoredConversionLabel, unconfiguredPaidAccounts} from './conversionLabels';
import {explorerIsInformes, explorerPath} from '../../../hooks/useReportModality';
import {anchorAttr} from '../../../guides/anchors';
import {ClientAvatar, clientIconUrl} from '../../elements/ClientAvatar';
import {Spinner} from '../../Spinner';
import {
    LOGO_ACCEPTED_TYPES,
    logoRejectionKey,
    logoUploadErrorMessage,
    uploadClientIcon,
    uploadedIconUrl,
} from './clientLogoUpload';

/**
 * El avatar del cliente en la ficha: su ícono o sus iniciales (`ClientAvatar`, el mismo del Listado),
 * y el lugar donde se SUBE el ícono (D8 de la tanda iVirtual, 09/10). Clic → selector de archivo →
 * `client/<id>/icon/`, que lo persiste solo: el avatar cambia en el acto. Es el ícono que ve sólo el
 * equipo, NO el logo del portal (D11, ronda 2): ése se elige en Portal › Apariencia.
 *
 * Con el cliente desactivado no se ofrece: el endpoint sólo acepta clientes activos y el gesto
 * terminaría en un error.
 */
function HeaderAvatar({client}) {
    const {t} = useTranslation();
    const inputRef = useRef(null);
    const hintId = useId();
    // El ícono recién subido, atado al cliente: la ruta no remonta al pasar de un cliente a otro, y el
    // de uno no puede quedar pegado en el avatar del siguiente.
    const [uploaded, setUploaded] = useState(null);
    const [uploading, setUploading] = useState(false);

    const logoUrl = uploaded && uploaded.clientId === client.id ? uploaded.url : clientIconUrl(client);
    const avatar = (
        <ClientAvatar id={client.id} name={client.name} logoUrl={logoUrl} className="pnl-header__avatar"/>
    );
    if (client.active === false) return avatar;

    const actionLabel = logoUrl ? t('client_panel.avatar_change') : t('client_panel.avatar_upload');

    const handleFile = (file) => {
        if (!file || uploading) return;
        const rejection = logoRejectionKey(file);
        if (rejection) {
            toast.error(t(rejection));
            return;
        }
        const clientId = client.id;
        setUploading(true);
        uploadClientIcon(clientId, file).then((response) => {
            setUploading(false);
            const url = uploadedIconUrl(response);
            if (url) {
                setUploaded({clientId, url});
                toast.success(t('client_panel.avatar_uploaded'));
            } else {
                toast.error(logoUploadErrorMessage(response, t));
            }
        // `uploadWithProgress` no rechaza; esto es por si algo del medio tira, para que el avatar
        // no quede con el spinner para siempre.
        }).catch(() => {
            setUploading(false);
            toast.error(logoUploadErrorMessage(null, t));
        });
    };

    return (
        <span className="pnl-header__avatar-wrap">
            <button type="button"
                    className={`pnl-header__avatar-btn${uploading ? ' -uploading' : ''}`}
                    onClick={() => inputRef.current && inputRef.current.click()}
                    disabled={uploading}
                    aria-label={actionLabel}
                    aria-describedby={hintId}
                    data-testid="pnl-avatar-upload">
                {avatar}
                <span className="pnl-header__avatar-edit" aria-hidden="true">
                    {uploading ? <Spinner color="positive very-small"/> : <PencilSimple size={11} weight="bold"/>}
                </span>
            </button>
            <span className="warning-tooltip -below-left">
                <span className="warning-tooltip-body">
                    <span className="warning-tooltip-text" id={hintId}>
                        <strong>{actionLabel}</strong>
                        {' · '}
                        {t('client_panel.avatar_hint')}
                    </span>
                </span>
            </span>
            <input ref={inputRef} type="file" accept={LOGO_ACCEPTED_TYPES} hidden tabIndex={-1}
                   data-testid="pnl-avatar-input"
                   onChange={(event) => {
                       handleFile(event.target.files && event.target.files[0]);
                       // Elegir el mismo archivo otra vez (después de un error) tiene que disparar.
                       event.target.value = '';
                   }}/>
        </span>
    );
}

// GOTCHA: Meta se representa como 'FACEBOOK' en todo el FE (`channels.js`). Un 'META' crudo cae por
// todos los `if` de ChannelLogo y no renderiza NADA, sin error — por eso se normaliza acá.
function normalizeChannel(channel) {
    return channel === 'META' ? FACEBOOK_CHANNEL : channel;
}

// Canales efectivamente conectados = los que tienen al menos una cuenta publicitaria o una tienda.
// `platforms.channels` es la lista de canales habilitados para la agencia, no los conectados.
function connectedChannels(platforms) {
    if (!platforms) return [];
    const sources = [...(platforms.client_ad_accounts || []), ...(platforms.client_stores || [])];
    const seen = [];
    sources.forEach((item) => {
        const channel = normalizeChannel(item && item.channel);
        // La familia de rentabilidad (Escalafy) no es una plataforma: la card de Plataformas tampoco la lista.
        if (!channel || !CHANNEL_INFO_BY_CHANNEL[channel] || seen.includes(channel)
            || ChannelsHelper.channelIsProfitability(channel)) return;
        seen.push(channel);
    });
    return seen;
}

const HIGHLIGHT_CLASS = '-highlight';
const HIGHLIGHT_MS = 1600;
// Un solo resaltado vivo: dos clics seguidos no dejan un timer viejo que lo apague antes de tiempo.
let highlightTimer = null;

/**
 * Baja hasta la card de Plataformas y la resalta un momento, para que se vea adónde se llegó. La
 * card lleva el id también mientras carga (su spinner), así que el clic siempre tiene destino
 * salvo que el Panel entero haya fallado.
 */
export function goToPlatforms() {
    if (typeof document === 'undefined') return;
    const card = document.getElementById(PLATFORMS_CARD_ID);
    if (!card) return;
    if (card.scrollIntoView) card.scrollIntoView({behavior: 'smooth', block: 'start'});
    card.classList.add(HIGHLIGHT_CLASS);
    clearTimeout(highlightTimer);
    highlightTimer = setTimeout(() => card.classList.remove(HIGHLIGHT_CLASS), HIGHLIGHT_MS);
}

/**
 * Encabezado del Panel: quién es el cliente (avatar + nombre + canales conectados), qué conversión
 * está mirando Dashbo, y las acciones de arranque (explorar, vincular una cuenta, crear una línea).
 *
 * El payload puede ser null mientras carga: el header se dibuja igual, con un skeleton en lugar del
 * nombre, para que la pantalla no salte cuando llegan los datos.
 */
export function PanelHeader({panel, clientId, onEditConversion, onNewLine, newLineGuideAnchor = {}}) {
    const {t} = useTranslation();

    const client = panel?.client;
    const conversion = panel?.monitored_conversion;
    const channels = connectedChannels(panel?.platforms);

    // El chip sólo existe si hay algo que monitorear: sin cuentas paid el backend manda
    // `mode: null` y el chip no va (contrato del agregado). El texto lo resuelve el MISMO helper
    // que usa el modal (SPECIFIC → la conversión elegida · ALL_PRIMARY → todas las primarias ·
    // MIXED → personalizada por cuenta): dos textos distintos para el mismo dato serían dos
    // verdades sobre qué mira Dashbo.
    const conversionLabel = monitoredConversionLabel(conversion, t);
    const showConversion = !!conversionLabel;

    // El veredicto agregado no distingue "todas las primarias" de "todas las primarias, pero una
    // cuenta no cuenta nada": las dos se rotulan igual. Con una cuenta sin configurar el chip pasa
    // a estado de atención y lo dice, porque si no el único lugar donde eso se ve es adentro del
    // modal, y hay que sospecharlo para abrirlo.
    const sinConfigurar = unconfiguredPaidAccounts(panel?.platforms?.client_ad_accounts).length;

    return (
        <header className="pnl-header">
            <div className="pnl-header__identity">
                {client
                    ? <HeaderAvatar client={client}/>
                    : <span className="pnl-header__avatar -skeleton"/>}

                <div className="pnl-header__txt">
                    {client
                        ? <h1 className="pnl-header__name">{client.name}</h1>
                        : <span className="pnl-header__name-skeleton"/>}

                    <div className="pnl-header__meta">
                        {channels.length > 0 && (
                            <span className="pnl-header__channels">
                                {channels.map((channel) => (
                                    <ChannelLogo key={channel} channel={channel} height={18} width={18}
                                                 hspace={0} vspace={0} marginLeft={0} padding={0}/>
                                ))}
                            </span>
                        )}

                        {showConversion && (
                            <button type="button"
                                    className={`pnl-header__conversion ${sinConfigurar ? '-pendiente' : ''}`}
                                    onClick={onEditConversion}
                                    {...anchorAttr('panel_conversion')}>
                                <Target size={14}/>
                                {t('client_panel.header_conversion', 'Conversión: {{label}}', {
                                    label: conversionLabel,
                                    interpolation: {escapeValue: false},
                                })}
                                {sinConfigurar > 0 && (
                                    <span className="pnl-header__conversion-pending">
                                        {t('client_panel.header_conversion_pending', {count: sinConfigurar})}
                                    </span>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="pnl-header__actions">
                {/* Con el cliente ya seleccionado: Informes es la solapa de esta ficha (card
                    7yIKxqZj) y el Explorador lee `?client_id=` (mismo mecanismo que la pestaña). Sin
                    el cliente caía en el último que el usuario hubiera mirado, que es justo el que
                    no está mirando ahora. */}
                <Link className="pnl-btn pnl-btn--ghost"
                      to={explorerIsInformes() ? `/client/${clientId}/informes` : `${explorerPath()}?client_id=${clientId}`}>
                    <Binoculars size={16}/>
                    {explorerIsInformes()
                        ? t('client_panel.header_informes')
                        : t('client_panel.header_explore')}
                </Link>
                {/* Acceso directo a Plataformas (card jaM5imSj): la card queda más abajo y había que
                    ir a buscarla. Baja hasta ahí y la resalta; cada canal tiene su «Vincular». */}
                <button type="button" className="pnl-btn pnl-btn--ghost" onClick={goToPlatforms}>
                    <Plugs size={16}/>
                    {t('client_panel.header_link_account')}
                </button>
                {/* `onNewLine` lo cablea el shell: abre el `AddBusinessUnitModal` de la ficha vieja,
                    montado allá con un ref (el modal maneja su propio estado de apertura). */}
                <button type="button" className="pnl-btn pnl-btn--primary" onClick={onNewLine}
                        {...newLineGuideAnchor}>
                    <Plus size={16}/>
                    {t('client_panel.header_new_line', 'Nueva línea de seguimiento')}
                </button>
            </div>
        </header>
    );
}
